import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { toTimeInputValue } from '../utils/time';

const generateLocalId = (() => {
  let counter = 0;
  return () => {
    counter += 1;
    return `local-shift-${Date.now()}-${counter}`;
  };
})();

const createShiftEntry = () => ({
  id: generateLocalId(),
  shiftId: '',
  timeFrom: '',
  timeTo: '',
  isPrimary: false,
  displayOrder: 0,
});

const normalizeEntries = (entries = []) => {
  if (!Array.isArray(entries)) {
    return [];
  }

  const sanitized = entries
    .map((entry, index) => {
      if (!entry) {
        return null;
      }

      const shiftId = entry.shiftId === null || entry.shiftId === undefined
        ? ''
        : String(entry.shiftId);
      const timeFrom = toTimeInputValue(entry.timeFrom ?? entry.shiftInTime ?? entry.startTime);
      const timeTo = toTimeInputValue(entry.timeTo ?? entry.shiftOutTime ?? entry.endTime);

      return {
        id: entry.id || `shift-${index}`,
        shiftId,
        timeFrom,
        timeTo,
        isPrimary: Boolean(entry.isPrimary),
        displayOrder: Number.isFinite(Number(entry.displayOrder))
          ? Number(entry.displayOrder)
          : index,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  if (sanitized.length === 0) {
    return [];
  }

  const primaryIndex = sanitized.findIndex((entry) => entry.isPrimary);
  return sanitized.map((entry, index) => ({
    ...entry,
    displayOrder: index,
    isPrimary: primaryIndex === -1 ? index === 0 : index === primaryIndex,
  }));
};

const WorkingShiftAllocation = ({
  shifts,
  onChange,
  shiftOptions,
  loading = false,
  error = null,
  disabled = false,
  validationError = null,
  allowPrimarySelection = true,
}) => {
  const entries = useMemo(() => normalizeEntries(shifts), [shifts]);

  const emitChange = (nextEntries) => {
    if (typeof onChange === 'function') {
      onChange(nextEntries);
    }
  };

  const ensurePrimary = (list) => {
    if (!Array.isArray(list) || list.length === 0) {
      return [];
    }

    const primaryIndex = list.findIndex((entry) => entry.isPrimary);
    if (primaryIndex === -1) {
      return list.map((entry, index) => ({
        ...entry,
        isPrimary: index === 0,
        displayOrder: index,
      }));
    }

    return list.map((entry, index) => ({
      ...entry,
      isPrimary: index === primaryIndex,
      displayOrder: index,
    }));
  };

  const handleAddShift = () => {
    const baseEntries = entries.length > 0 ? entries : [];
    const newEntry = {
      ...createShiftEntry(),
      isPrimary: baseEntries.length === 0,
      displayOrder: baseEntries.length,
    };
    emitChange(ensurePrimary([...baseEntries, newEntry]));
  };

  const handleRemoveShift = (index) => {
    const baseEntries = entries.length > 0 ? [...entries] : [];
    baseEntries.splice(index, 1);
    emitChange(ensurePrimary(baseEntries));
  };

  const handleShiftIdChange = (index, value) => {
    const baseEntries = entries.length > 0 ? [...entries] : [];
    const entry = baseEntries[index] ?? createShiftEntry();
    const selected = shiftOptions.find((option) => option.value === value);
    baseEntries[index] = {
      ...entry,
      shiftId: value,
      timeFrom:
        entry.timeFrom && entry.timeFrom.trim() !== ''
          ? entry.timeFrom
          : toTimeInputValue(selected?.inTime),
      timeTo:
        entry.timeTo && entry.timeTo.trim() !== ''
          ? entry.timeTo
          : toTimeInputValue(selected?.outTime),
      displayOrder: index,
    };
    emitChange(ensurePrimary(baseEntries));
  };

  const handleTimeChange = (index, field, value) => {
    const baseEntries = entries.length > 0 ? [...entries] : [];
    const entry = baseEntries[index] ?? createShiftEntry();
    baseEntries[index] = {
      ...entry,
      [field]: value,
      displayOrder: index,
    };
    emitChange(ensurePrimary(baseEntries));
  };

  const handlePrimaryChange = (index) => {
    if (!allowPrimarySelection) {
      return;
    }

    const baseEntries = entries.length > 0 ? [...entries] : [];
    const nextEntries = baseEntries.map((entry, idx) => ({
      ...entry,
      isPrimary: idx === index,
      displayOrder: idx,
    }));
    emitChange(nextEntries);
  };

  return (
    <div className="working-shift-allocation">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Shift Allocations</h6>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={handleAddShift}
          disabled={disabled}
        >
          Add Shift
        </button>
      </div>

      {loading && (
        <div className="alert alert-info" role="status">
          Loading shifts…
        </div>
      )}

      {error && !loading && (
        <div className="alert alert-warning" role="alert">
          {error}
        </div>
      )}

      {entries.length === 0 && !loading && (
        <p className="text-muted fst-italic">No shifts added yet. Use the button above to add one.</p>
      )}

      {entries.map((entry, index) => {
        const primaryId = `primary-shift-${entry.id ?? index}`;
        return (
          <div key={entry.id ?? index} className="border rounded p-3 mb-3">
            <div className="row g-3 align-items-end">
              <div className="col-md-5">
                <label className="form-label" htmlFor={`shift-select-${entry.id ?? index}`}>
                  Shift
                </label>
                <select
                  id={`shift-select-${entry.id ?? index}`}
                  className="form-select"
                  value={entry.shiftId ?? ''}
                  onChange={(event) => handleShiftIdChange(index, event.target.value)}
                  disabled={disabled}
                >
                  <option value="">Select shift (optional)</option>
                  {shiftOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label" htmlFor={`shift-from-${entry.id ?? index}`}>
                  Start Time
                </label>
                <input
                  id={`shift-from-${entry.id ?? index}`}
                  type="time"
                  className="form-control"
                  value={entry.timeFrom ?? ''}
                  onChange={(event) => handleTimeChange(index, 'timeFrom', event.target.value)}
                  disabled={disabled}
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" htmlFor={`shift-to-${entry.id ?? index}`}>
                  End Time
                </label>
                <input
                  id={`shift-to-${entry.id ?? index}`}
                  type="time"
                  className="form-control"
                  value={entry.timeTo ?? ''}
                  onChange={(event) => handleTimeChange(index, 'timeTo', event.target.value)}
                  disabled={disabled}
                />
              </div>
              <div className="col-md-1 d-flex flex-column align-items-center justify-content-center">
                {allowPrimarySelection && (
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="primaryShift"
                      id={primaryId}
                      checked={Boolean(entry.isPrimary)}
                      onChange={() => handlePrimaryChange(index)}
                      disabled={disabled}
                    />
                    <label className="form-check-label small" htmlFor={primaryId}>
                      Primary
                    </label>
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-link text-danger p-0 mt-2"
                  onClick={() => handleRemoveShift(index)}
                  disabled={disabled}
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {validationError && (
        <div className="text-danger small" role="alert">
          {validationError}
        </div>
      )}
    </div>
  );
};

WorkingShiftAllocation.propTypes = {
  shifts: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  shiftOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      inTime: PropTypes.string,
      outTime: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
  validationError: PropTypes.string,
  allowPrimarySelection: PropTypes.bool,
};

WorkingShiftAllocation.defaultProps = {
  shifts: [],
  shiftOptions: [],
  loading: false,
  error: null,
  disabled: false,
  validationError: null,
  allowPrimarySelection: true,
};

export default WorkingShiftAllocation;
